import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Plus, FileText, Receipt, FolderKanban, Smile, Send, Clock, CheckCircle, XCircle, User, AlertCircle, MessageSquare, Phone, Mail, Building, Target } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerPortal = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [estimateRequests, setEstimateRequests] = useState([]);
  const [joke, setJoke] = useState('');
  const [loading, setLoading] = useState(true);
  const [profileCompletion, setProfileCompletion] = useState({ percentage: 0, completed: false });
  const [showProfileModal, setShowProfileModal] = useState(false);

  // Customer messaging state  
  const [newMessage, setNewMessage] = useState('');
  const [messageType, setMessageType] = useState('question');
  const [messagePriority, setMessagePriority] = useState('normal');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [customerMessages, setCustomerMessages] = useState([]);

  useEffect(() => {
    loadCustomerData();
    loadJoke();
    loadProfileCompletion();
    if (user && user.id) {
      loadCustomerMessages();
    }
  }, []);

  const loadCustomerData = async () => {
    try {
      const [quotesRes, invoicesRes, projectsRes, estimatesRes] = await Promise.all([
        axios.get(`${API}/customer/quotes`),
        axios.get(`${API}/customer/invoices`),
        axios.get(`${API}/customer/projects`),
        axios.get(`${API}/estimates/requests/my`)
      ]);
      
      setQuotes(quotesRes.data);
      setInvoices(invoicesRes.data);
      setProjects(projectsRes.data);
      setEstimateRequests(estimatesRes.data);
    } catch (error) {
      console.error('Failed to load customer data', error);
    } finally {
      setLoading(false);
    }
  };

  const loadJoke = async (theme = null) => {
    try {
      const url = theme ? `${API}/customer/joke?theme=${theme}` : `${API}/customer/joke`;
      const response = await axios.get(url);
      setJoke(response.data.joke);
    } catch (error) {
      console.error('Failed to load joke');
    }
  };

  const loadProfileCompletion = async () => {
    try {
      const response = await axios.get(`${API}/customer/profile/completion`);
      setProfileCompletion(response.data);
      
      // Show modal if profile is not completed
      if (!response.data.completed) {
        setShowProfileModal(true);
      }
    } catch (error) {
      console.error('Failed to load profile completion');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      Pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      Reviewing: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      Quoted: 'bg-green-500/20 text-green-400 border-green-500/30',
      Declined: 'bg-red-500/20 text-red-400 border-red-500/30',
      Paid: 'bg-green-500/20 text-green-400 border-green-500/30',
      Partial: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      Unpaid: 'bg-red-500/20 text-red-400 border-red-500/30',
    };
    
    return styles[status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    setSendingMessage(true);
    try {
      const messageData = {
        message: newMessage,
        message_type: messageType,
        priority: messagePriority,
        is_customer_message: true // Customer sending to staff
      };

      // Send message to customer's own ID (they're messaging the company)
      await axios.post(`${API}/crm/customers/${user.id}/messages`, messageData);
      
      toast.success('Message sent successfully! Our team will respond soon.');
      setNewMessage('');
      setMessageType('question');
      setMessagePriority('normal');
      
      // Optionally reload messages to show the new one
      loadCustomerMessages();
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
      console.error('Send message error:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  const loadCustomerMessages = async () => {
    try {
      const response = await axios.get(`${API}/crm/customers/${user.id}/messages`);
      setCustomerMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to load messages:', error);
      setCustomerMessages([]);
    }
  };

  const handleDeleteEstimate = async (estimateId) => {
    if (!window.confirm('Are you sure you want to delete this draft estimate? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/estimates/requests/${estimateId}`);
      toast.success('Draft estimate deleted successfully');
      // Reload the estimates
      loadCustomerData();
    } catch (error) {
      toast.error('Failed to delete estimate');
      console.error('Delete estimate error:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-primary text-xl font-mono uppercase tracking-widest">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase text-foreground mb-2">
            WELCOME, {user.name}
          </h1>
          <p className="text-sm font-mono uppercase tracking-wide text-muted-foreground">
            Customer Portal
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => navigate('/profile')}
            variant="outline"
            className="font-mono uppercase text-xs text-foreground font-bold border-2 hover:bg-muted"
          >
            <User size={14} className="mr-2" />
            My Profile
          </Button>
          <Button
            onClick={onLogout}
            variant="outline"
            className="font-mono uppercase text-xs text-foreground font-bold border-2 hover:bg-destructive hover:text-destructive-foreground"
          >
            Logout
          </Button>
        </div>
      </div>

      {/* Profile Completion Banner */}
      {!profileCompletion.completed && (
        <Card className="border-yellow-500/30 bg-yellow-500/10 mb-6">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <AlertCircle size={32} className="text-yellow-500 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-lg font-bold uppercase mb-2 text-foreground">Complete Your Profile</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Help us serve you better by completing your business profile. 
                  You're {profileCompletion.percentage}% complete!
                </p>
                <div className="w-full bg-border rounded-full h-2 mb-3">
                  <div 
                    className="bg-yellow-500 rounded-full h-2 transition-all duration-500"
                    style={{ width: `${profileCompletion.percentage}%` }}
                  />
                </div>
                <Button
                  onClick={() => navigate('/profile')}
                  variant="outline"
                  size="sm"
                  className="font-mono uppercase text-xs border-yellow-500/30 hover:bg-yellow-500/20"
                >
                  Complete Profile Now
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Joke of the Day */}
      <Card className="border-border mb-6 bg-gradient-to-r from-primary/10 to-blue-500/10">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <Smile size={32} className="text-primary flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-lg font-bold uppercase mb-2 text-foreground">Joke of the Day</h3>
              <p className="text-foreground italic mb-4">"{joke}"</p>
              
              {/* Joke Theme Buttons */}
              <div className="space-y-3">
                <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                  Choose Your Humor:
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                  <Button
                    onClick={() => loadJoke()}
                    variant="outline"
                    size="sm"
                    className="font-mono uppercase text-xs h-auto py-2"
                  >
                    Random
                  </Button>
                  <Button
                    onClick={() => loadJoke('welding')}
                    variant="outline"
                    size="sm"
                    className="font-mono uppercase text-xs h-auto py-2"
                  >
                    Welding
                  </Button>
                  <Button
                    onClick={() => loadJoke('fabrication')}
                    variant="outline"
                    size="sm"
                    className="font-mono uppercase text-xs h-auto py-2"
                  >
                    Fabrication
                  </Button>
                  <Button
                    onClick={() => loadJoke('dad')}
                    variant="outline"
                    size="sm"
                    className="font-mono uppercase text-xs h-auto py-2"
                  >
                    Dad Jokes
                  </Button>
                  <Button
                    onClick={() => loadJoke('workday')}
                    variant="outline"
                    size="sm"
                    className="font-mono uppercase text-xs h-auto py-2"
                  >
                    Workday
                  </Button>
                  <Button
                    onClick={() => loadJoke('technology')}
                    variant="outline"
                    size="sm"
                    className="font-mono uppercase text-xs h-auto py-2"
                  >
                    Tech & AI
                  </Button>
                  <Button
                    onClick={() => loadJoke('canadian')}
                    variant="outline"
                    size="sm"
                    className="font-mono uppercase text-xs h-auto py-2"
                  >
                    Canadian
                  </Button>
                  <Button
                    onClick={() => loadJoke('machine')}
                    variant="outline"
                    size="sm"
                    className="font-mono uppercase text-xs h-auto py-2"
                  >
                    Machines
                  </Button>
                  <Button
                    onClick={() => loadJoke('safety')}
                    variant="outline"
                    size="sm"
                    className="font-mono uppercase text-xs h-auto py-2"
                  >
                    Safety
                  </Button>
                  <Button
                    onClick={() => loadJoke('motivational')}
                    variant="outline"
                    size="sm"
                    className="font-mono uppercase text-xs h-auto py-2"
                  >
                    Motivational
                  </Button>
                  <Button
                    onClick={() => loadJoke('animal')}
                    variant="outline"
                    size="sm"
                    className="font-mono uppercase text-xs h-auto py-2"
                  >
                    Animals
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card className="border-border">
          <CardContent className="p-6">
            <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">
              My Quotes
            </p>
            <p className="text-3xl font-bold font-mono text-blue-500">{quotes.length}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-6">
            <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">
              My Invoices
            </p>
            <p className="text-3xl font-bold font-mono text-primary">{invoices.length}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-6">
            <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">
              My Projects
            </p>
            <p className="text-3xl font-bold font-mono text-green-500">{projects.length}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-6">
            <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">
              Pending Estimates
            </p>
            <p className="text-3xl font-bold font-mono text-yellow-500">
              {estimateRequests.filter(r => r.status === 'Pending').length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Request Estimate Button */}
      <div className="mb-6 flex gap-3">
        <Button
          onClick={() => navigate('/estimate-request')}
          variant="outline"
          className="font-mono uppercase tracking-wider"
        >
          <Plus size={16} className="mr-2" />
          Quick Form
        </Button>
        <Button
          onClick={() => navigate('/estimate-chat')}
          className="bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase tracking-wider"
        >
          <MessageSquare size={16} className="mr-2" />
          AI Chat Estimate
        </Button>
      </div>

      {/* Estimate Requests */}
      <Card className="border-border mb-6">
        <CardHeader>
          <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground">
            My Estimate Requests
          </CardTitle>
        </CardHeader>
        <CardContent>
          {estimateRequests.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No estimate requests yet. Submit one to get started!</p>
          ) : (
            <div className="space-y-3">
              {estimateRequests.map((request) => (
                <div key={request.id} className="p-4 border border-border rounded-sm hover:border-primary/30 transition-colors">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground">{request.project_name}</h3>
                      <p className="text-sm text-muted-foreground mt-1">{request.description}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-xs font-mono text-muted-foreground">
                          Type: {request.project_type || 'Not specified'}
                        </span>
                        <span className="text-xs font-mono text-muted-foreground">
                          Created: {formatDate(request.created_at)}
                        </span>
                        <span className={`px-2 py-1 rounded border font-mono text-xs uppercase ${getStatusBadge(request.status)}`}>
                          {request.status}
                        </span>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 ml-4">
                      {/* View/Edit Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/estimate-request?edit=${request.id}`)}
                        className="font-mono uppercase text-xs"
                      >
                        {request.status === 'draft' ? 'Edit' : 'View'}
                      </Button>
                      
                      {/* Delete Button (only for drafts) */}
                      {request.status === 'draft' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteEstimate(request.id)}
                          className="font-mono uppercase text-xs text-destructive hover:bg-destructive hover:text-destructive-foreground"
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  </div>
                  {request.notes && (
                    <div className="mt-3 p-3 bg-accent/50 rounded-sm border border-border">
                      <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1">Staff Notes:</p>
                      <p className="text-sm text-foreground">{request.notes}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* My Quotes */}
      <Card className="border-border mb-6">
        <CardHeader>
          <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground flex items-center gap-2">
            <FileText size={20} />
            My Quotes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {quotes.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No quotes yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Quote #</th>
                    <th className="text-left py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Project</th>
                    <th className="text-right py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Total</th>
                    <th className="text-center py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {quotes.map((quote) => (
                    <tr key={quote.id} className="border-b border-border hover:bg-accent/50 transition-colors">
                      <td className="py-3 px-2 font-mono">{quote.quote_number}</td>
                      <td className="py-3 px-2">{quote.project_name}</td>
                      <td className="py-3 px-2 text-right font-mono text-primary">
                        ${quote.total?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="py-3 px-2 text-center font-mono text-xs text-muted-foreground">
                        {formatDate(quote.date)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* My Projects */}
      <Card className="border-border mb-6">
        <CardHeader>
          <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground flex items-center gap-2">
            <FolderKanban size={20} />
            My Projects
          </CardTitle>
        </CardHeader>
        <CardContent>
          {projects.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No projects yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Project</th>
                    <th className="text-center py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Status</th>
                    <th className="text-center py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Start Date</th>
                    <th className="text-center py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((project) => (
                    <React.Fragment key={project.id}>
                      <tr className="border-b border-border hover:bg-accent/50 transition-colors">
                        <td className="py-3 px-2">
                          <div>
                            <p className="font-medium">{project.project_name}</p>
                            {project.milestones && project.milestones.length > 0 && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {project.milestones.filter(m => m.completed).length} of {project.milestones.length} milestones completed
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-2 text-center">
                          <span className={`px-2 py-1 rounded border font-mono text-xs uppercase ${getStatusBadge(project.status)}`}>
                            {project.status}
                          </span>
                        </td>
                        <td className="py-3 px-2 text-center font-mono text-xs text-muted-foreground">
                          {project.start_date ? formatDate(project.start_date) : 'TBD'}
                        </td>
                        <td className="py-3 px-2 text-center">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/project/${project.id}`)}
                            className="font-mono uppercase text-xs"
                          >
                            <MessageSquare size={14} className="mr-1" />
                            View & Message
                          </Button>
                        </td>
                      </tr>
                      {project.milestones && project.milestones.length > 0 && (
                        <tr className="border-b border-border bg-accent/20">
                          <td colSpan="4" className="py-3 px-4">
                            <div className="space-y-2">
                              <p className="text-xs font-mono uppercase text-muted-foreground mb-2">Project Milestones</p>
                              {project.milestones.map((milestone) => (
                                <div key={milestone.id} className="flex items-center gap-3 py-2 px-3 border border-border rounded bg-background">
                                  <CheckCircle 
                                    size={16} 
                                    className={milestone.completed ? "text-green-500" : "text-muted-foreground"}
                                  />
                                  <span className={`text-sm flex-1 ${milestone.completed ? 'line-through text-muted-foreground' : ''}`}>
                                    {milestone.description}
                                  </span>
                                  <div className="flex items-center gap-3">
                                    <span className="text-xs font-mono text-muted-foreground">
                                      {(milestone.hours_spent || 0).toFixed(1)}h / {milestone.hours_estimated}h
                                    </span>
                                    <div className="w-20 bg-border rounded-full h-2">
                                      <div 
                                        className={`rounded-full h-2 ${milestone.completed ? 'bg-green-500' : 'bg-primary'}`}
                                        style={{ 
                                          width: `${Math.min(((milestone.hours_spent || 0) / milestone.hours_estimated) * 100, 100)}%` 
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* My Invoices */}
      <Card className="border-border mb-6">
        <CardHeader>
          <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground flex items-center gap-2">
            <Receipt size={20} />
            My Invoices
          </CardTitle>
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No invoices yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Invoice #</th>
                    <th className="text-right py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Total</th>
                    <th className="text-right py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Paid</th>
                    <th className="text-right py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Balance</th>
                    <th className="text-center py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Status</th>
                    <th className="text-center py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.id} className="border-b border-border hover:bg-accent/50 transition-colors">
                      <td className="py-3 px-2 font-mono">{invoice.invoice_number}</td>
                      <td className="py-3 px-2 text-right font-mono">
                        ${invoice.total?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="py-3 px-2 text-right font-mono text-green-500">
                        ${invoice.amount_paid?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="py-3 px-2 text-right font-mono text-red-500">
                        ${invoice.balance_due?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="py-3 px-2 text-center">
                        <span className={`px-2 py-1 rounded border font-mono text-xs uppercase ${getStatusBadge(invoice.status)}`}>
                          {invoice.status}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-center font-mono text-xs text-muted-foreground">
                        {formatDate(invoice.invoice_date)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Contact PSM Team - Main Feature */}
      <Card className="border-primary/30 bg-gradient-to-r from-primary/10 to-primary/5 mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-xl font-bold uppercase">
            <MessageSquare className="text-primary" size={24} />
            Contact PSM Team
            <Badge variant="secondary" className="ml-auto">Message Center</Badge>
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Need help with your project, have questions, or want to discuss requirements? 
            Send us a message and our team will respond promptly.
          </p>
        </CardHeader>
        <CardContent>
          {/* Quick Message Form */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-mono uppercase">Message Type</Label>
                  <select
                    value={messageType}
                    onChange={(e) => setMessageType(e.target.value)}
                    className="w-full px-3 py-2 bg-background border border-input rounded-sm text-sm"
                  >
                    <option value="question">General Question</option>
                    <option value="project">Project Inquiry</option>
                    <option value="quote">Quote Request</option>
                    <option value="support">Support Needed</option>
                    <option value="feedback">Feedback</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Priority</Label>
                  <select
                    value={messagePriority}
                    onChange={(e) => setMessagePriority(e.target.value)}
                    className="w-full px-3 py-2 bg-background border border-input rounded-sm text-sm"
                  >
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
              
              <div>
                <Label className="text-xs font-mono uppercase">Your Message</Label>
                <Textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Tell us how we can help you..."
                  className="min-h-[100px] mt-1"
                />
              </div>
              
              <Button 
                onClick={handleSendMessage}
                disabled={!newMessage.trim() || sendingMessage}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Send size={16} className="mr-2" />
                {sendingMessage ? 'Sending Message...' : 'Send Message to PSM Team'}
              </Button>
            </div>
            
            {/* Contact Info & Response Time */}
            <div className="space-y-4">
              <Card className="bg-accent/20 border-accent">
                <CardContent className="p-4">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Phone size={16} className="text-primary" />
                    Response Times
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Normal Priority:</span>
                      <span className="font-mono">24-48 hours</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">High Priority:</span>
                      <span className="font-mono">4-12 hours</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Urgent:</span>
                      <span className="font-mono text-red-500">1-4 hours</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4">
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <Mail size={16} className="text-blue-600" />
                    Other Ways to Reach Us
                  </h3>
                  <div className="space-y-1 text-sm">
                    <p><strong>Email:</strong> info@psmwelding.com</p>
                    <p><strong>Phone:</strong> (555) 123-4567</p>
                    <p><strong>Business Hours:</strong> Mon-Fri 7AM-5PM</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card className="border-primary/30 bg-primary/5 mb-6">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-xl font-bold uppercase mb-2 text-foreground">Need a New Project Estimate?</h3>
              <p className="text-sm text-foreground/80 mb-3 font-medium">
                Get a detailed quote for your welding and fabrication project. Our AI assistant will help guide you through the process.
              </p>
              <ul className="text-sm text-foreground/90 space-y-1 mb-4 font-medium">
                <li>✓ Choose from Quick, Detailed, or Guided Survey forms</li>
                <li>✓ AI-powered assistant to answer your questions in real-time</li>
                <li>✓ Upload drawings, photos, specifications, and reference documents</li>
                <li>✓ Specify materials, dimensions, budget, and timeline preferences</li>
                <li>✓ Get a professional response from our team within 24 hours</li>
              </ul>
            </div>
            <Button
              onClick={() => navigate('/estimate-request')}
              size="lg"
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase tracking-wider shadow-lg ml-6"
            >
              <FileText className="mr-2" size={20} />
              Request Estimate
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Profile Completion Modal - Blocking */}
      {showProfileModal && !profileCompletion.completed && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <Card className="border-primary/30 bg-card max-w-2xl w-full shadow-2xl">
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/20 rounded-full mb-4">
                  <Building size={32} className="text-primary" />
                </div>
                <h2 className="text-3xl font-black tracking-tight uppercase text-foreground mb-2">
                  Welcome to PSM Welding!
                </h2>
                <p className="text-lg text-muted-foreground">
                  Let's get to know your business
                </p>
              </div>

              <div className="bg-accent/30 border border-border rounded-sm p-6 mb-6">
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <Target size={20} className="text-primary" />
                  Why Complete Your Profile?
                </h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Better Quotes:</strong> Help us understand your specific needs for accurate pricing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Faster Service:</strong> Pre-filled information speeds up future projects</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Personalized Experience:</strong> Receive recommendations tailored to your industry</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Priority Support:</strong> Complete profiles get dedicated account management</span>
                  </li>
                </ul>
              </div>

              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-mono uppercase text-muted-foreground">
                    Profile Completion
                  </span>
                  <span className="text-sm font-bold text-primary">
                    {profileCompletion.percentage}%
                  </span>
                </div>
                <div className="w-full bg-border rounded-full h-3">
                  <div 
                    className="bg-gradient-to-r from-primary to-blue-500 rounded-full h-3 transition-all duration-500"
                    style={{ width: `${profileCompletion.percentage}%` }}
                  />
                </div>
              </div>

              <div className="text-center text-sm text-muted-foreground mb-6">
                <p>⏱️ Takes only <strong>2-3 minutes</strong> to complete</p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => navigate('/profile')}
                  size="lg"
                  className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase tracking-wider shadow-lg"
                >
                  <User className="mr-2" size={18} />
                  Complete Profile Now
                </Button>
                <Button
                  onClick={() => setShowProfileModal(false)}
                  size="lg"
                  variant="outline"
                  className="font-mono uppercase tracking-wider"
                >
                  I'll Do This Later
                </Button>
              </div>

              <p className="text-xs text-center text-muted-foreground mt-4">
                You can access your profile anytime from the top right corner
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CustomerPortal;
